import type { ComponentType } from "react";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import {
  Phone,
  PhoneIphone,
  Email,
  Language,
  SmsOutlined,
  FaxOutlined,
  Facebook,
  Instagram,
  YouTube,
  Twitter,
  LinkedIn,
  MusicNote,
  Map,
} from "@mui/icons-material";

export type MuiIcon = ComponentType<SvgIconProps>;

export type AllianceQuickLinkItem = {
  key: string;
  href: string;
  ariaLabel: string;
  Icon: MuiIcon;
};

export function nonEmpty(s: unknown): string | null {
  if (s == null) return null;
  const t = String(s).trim();
  return t.length > 0 ? t : null;
}

/** Za usporedbu jesu li dva prikazana broja ista (ignorira razmake i ne-znamenke). */
export function normalizeContactCompare(s: string): string {
  return s
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[^\d+]/g, "");
}

/** Ulica, poštanski broj i grad, županija – jedna redčica. */
export function formatFullAddress(address: any): string | null {
  if (!address || typeof address !== "object") return null;
  const street = nonEmpty(address.street);
  const postal = nonEmpty(address.postal ?? address.postal_code);
  const city = nonEmpty(address.city);
  const county = nonEmpty(address.county);
  const postalCity = [postal, city].filter(Boolean).join(" ");
  const pieces = [street, postalCity || null, county].filter(
    Boolean,
  ) as string[];
  if (pieces.length === 0) return null;
  return pieces.join(", ");
}

/** Isti obrazac kao `ph-zgss/src/ui/utils.js` (`generateGoogleMapsLink`). */
export function googleMapsSearchHref(address: unknown): string | null {
  if (!address || typeof address !== "object") return null;
  const a = address as Record<string, unknown>;
  const parts = [
    nonEmpty(a.street),
    nonEmpty(a.postal ?? a.postal_code),
    nonEmpty(a.city),
    nonEmpty(a.county),
  ].filter(Boolean) as string[];
  if (parts.length === 0) return null;
  const query = encodeURIComponent(parts.join(", "));
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

export function hostnameFromUrl(url: string): string {
  try {
    const u = url.startsWith("http") ? url : `https://${url}`;
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return url.length > 40 ? `${url.slice(0, 37)}…` : url;
  }
}

export function normalizeHttpUrl(raw: string): string {
  let t = raw.trim();
  if (!t) return t;
  if (/^mailto:/i.test(t)) return t;
  t = t.replace(/^\/\//, "https://");
  if (/^https?:\/\//i.test(t)) return t;
  if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(t)) return `https://${t}`;
  return t;
}

function firstPhoneSegment(s: string): string {
  return s.split(/(?:\s*\/\s*|\s+ili\s+)/i)[0]?.trim() ?? s;
}

function dialCore(s: string): string | null {
  let compact = firstPhoneSegment(s).replace(/\s+/g, "").trim();
  if (compact.startsWith("00")) compact = `+${compact.slice(2)}`;
  const cleaned = compact.replace(/[^\d+]/g, "");
  const digitsOnly = cleaned.replace(/\D/g, "");
  if (digitsOnly.length < 5) return null;
  return cleaned;
}

export function telHref(s: string): string | null {
  const c = dialCore(s);
  return c ? `tel:${c}` : null;
}

export function smsHref(s: string): string | null {
  const c = dialCore(s);
  return c ? `sms:${c}` : null;
}

export function mailtoHref(s: string): string | null {
  const e = nonEmpty(s);
  return e ? `mailto:${e.replace(/^mailto:/i, "")}` : null;
}

function socialIconForHref(href: string): MuiIcon {
  const h = href.toLowerCase();
  if (h.includes("facebook.com")) return Facebook;
  if (h.includes("instagram.com")) return Instagram;
  if (h.includes("youtube.com") || h.includes("youtu.be")) return YouTube;
  if (h.includes("linkedin.com")) return LinkedIn;
  if (h.includes("twitter.com") || h.includes("x.com") || h.includes("t.co/"))
    return Twitter;
  if (h.includes("tiktok.com")) return MusicNote;
  return Language;
}

function socialAriaLabel(href: string): string {
  const h = href.toLowerCase();
  if (h.includes("facebook.com")) return "Facebook";
  if (h.includes("instagram.com")) return "Instagram";
  if (h.includes("youtube.com") || h.includes("youtu.be")) return "YouTube";
  if (h.includes("linkedin.com")) return "LinkedIn";
  if (h.includes("twitter.com") || h.includes("x.com")) return "Twitter";
  if (h.includes("tiktok.com")) return "TikTok";
  return "Web stranica";
}

/** Jedna izvorna lista za footer kartice i zaglavlje stranice detalja. */
export function buildAllianceQuickLinks(
  alliance: any,
): AllianceQuickLinkItem[] {
  const out: AllianceQuickLinkItem[] = [];
  const seenUri = new Set<string>();
  const seenHttp = new Set<string>();

  const pushUri = (
    key: string,
    href: string | null,
    label: string,
    Icon: MuiIcon,
  ) => {
    if (!href) return;
    if (seenUri.has(href)) return;
    seenUri.add(href);
    out.push({ key, href, ariaLabel: label, Icon });
  };

  const pushHttp = (key: string, raw: string, label?: string) => {
    const href = normalizeHttpUrl(raw);
    if (!href.startsWith("http")) return;
    const norm = href.toLowerCase().replace(/\/$/, "");
    if (seenHttp.has(norm)) return;
    seenHttp.add(norm);
    const Icon = socialIconForHref(href);
    const aria = label ?? socialAriaLabel(href);
    out.push({ key, href, ariaLabel: aria, Icon });
  };

  const contact = alliance.contact ?? {};
  const other = alliance.other ?? {};
  const details = alliance.details ?? {};

  const landline = nonEmpty(contact.phone);
  const mobile = nonEmpty(contact.mobile);
  const fax = nonEmpty(contact.fax);
  const mail = nonEmpty(contact.mail);
  const site = nonEmpty(details.link);

  const sameDigits =
    landline &&
    mobile &&
    normalizeContactCompare(landline) === normalizeContactCompare(mobile);

  if (landline) {
    pushUri("tel-landline", telHref(landline), `Telefon: ${landline}`, Phone);
  }
  if (mobile && !sameDigits) {
    pushUri("tel-mobile", telHref(mobile), `Mobitel: ${mobile}`, PhoneIphone);
  }
  if (mobile) {
    pushUri("sms", smsHref(mobile), `SMS: ${mobile}`, SmsOutlined);
  }
  if (mail) pushUri("mail", mailtoHref(mail)!, `E-pošta: ${mail}`, Email);
  const faxTel = fax ? telHref(fax) : null;
  if (faxTel) pushUri("fax", faxTel, `Faks: ${fax}`, FaxOutlined);
  if (site) pushHttp("website", site, hostnameFromUrl(site));

  const mapsHref = googleMapsSearchHref(alliance.address);
  if (mapsHref) {
    const mapsNorm = mapsHref.toLowerCase();
    if (!seenHttp.has(mapsNorm)) {
      seenHttp.add(mapsNorm);
      out.push({
        key: "maps",
        href: mapsHref,
        ariaLabel: "Lokacija na karti",
        Icon: Map,
      });
    }
  }

  const OTHER_SOCIAL_KEYS = [
    "facebook",
    "instagram",
    "youtube",
    "linkedin",
    "twitter",
    "tiktok",
  ] as const;
  for (const k of OTHER_SOCIAL_KEYS) {
    const raw = nonEmpty(other[k]);
    if (raw) pushHttp(`other-${k}`, raw);
  }

  const pratiteNas = other.pratiteNas;
  if (pratiteNas && typeof pratiteNas === "object") {
    for (const [k, raw] of Object.entries(
      pratiteNas as Record<string, unknown>,
    )) {
      const s = nonEmpty(raw);
      if (s) pushHttp(`pratiteNas-${k}`, s);
    }
  }

  return out;
}
